// Story 1.29 — Users panel renderer.
//
// Plain HTML/CSS visualization (no charts library at v1). Two
// sections: MAU per month (single bar list) + signup funnel per month
// (three-segment stacked bar list).
//
// Color palette per UX-DR26 / F1-telemetry guidance:
//   - registered = brand-forest-300 (neutral signal)
//   - email_verified = brand-forest-700 (deeper "qualified" signal)
//   - first_dossier_purchased = tier-premium-fill (treasured-customer)
//     rendered as `—` placeholder until Epic 2 ships dossier.purchased.

import type { UsersPanelData } from "./useAdminMetrics";

type Props = {
  data: UsersPanelData;
};

function fmtMonth(label: string): string {
  // "2026-05" -> "May 2026" for screen-reader-friendly summary line.
  const [yearStr, monthStr] = label.split("-");
  const year = Number(yearStr);
  const monthIdx = Number(monthStr) - 1;
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${monthNames[monthIdx] ?? monthStr} ${year}`;
}

export function UsersPanel({ data }: Props) {
  const allZero = data.mau_by_month.every((r) => r.count === 0);
  const maxMau = Math.max(1, ...data.mau_by_month.map((r) => r.count));
  const maxFunnel = Math.max(
    1,
    ...data.signup_funnel.flatMap((r) => [r.registered, r.email_verified])
  );

  // Most recent month with at least one event. Used for the narrative line.
  const latestMonth = [...data.signup_funnel]
    .reverse()
    .find((r) => r.registered > 0 || r.email_verified > 0);

  return (
    <div className="space-y-8">
      {/* MAU section */}
      <section className="space-y-3">
        <header>
          <h2 className="text-xl font-heading font-semibold">
            Monthly Active Users (logins)
          </h2>
          <p className="text-sm opacity-70 mt-1">
            Distinct users with at least one login per month — under-counts
            users with rolling sessions; precise count when
            <code className="font-mono"> sessions.last_seen_at </code>
            integration lands.
          </p>
        </header>

        {allZero ? (
          <p className="text-sm opacity-70 italic">
            No login activity in the last 12 months yet — first MAU bar
            appears when a user logs in.
          </p>
        ) : (
          <div className="space-y-1.5">
            {data.mau_by_month.map((row) => (
              <div
                key={row.month}
                className="flex items-center gap-3"
                title={`${row.month}: ${row.count} MAU`}
              >
                <span className="font-mono text-xs w-20 opacity-80">
                  {row.month}
                </span>
                <div className="flex-1 h-4 bg-brand-paper-100 rounded-sm overflow-hidden">
                  <div
                    style={{ width: `${(row.count / maxMau) * 100}%` }}
                    className="h-full bg-brand-forest-700 rounded-sm"
                  />
                </div>
                <span className="font-mono text-sm w-12 text-right">
                  {row.count}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Signup funnel section */}
      <section className="space-y-3">
        <header>
          <h2 className="text-xl font-heading font-semibold">Signup funnel</h2>
          <p className="text-sm opacity-70 mt-1">
            Per-month registration → email-verification → first dossier
            purchased.
          </p>
        </header>

        {latestMonth ? (
          <p className="text-sm">
            <strong>{fmtMonth(latestMonth.month)}:</strong>{" "}
            {latestMonth.registered} registered, {latestMonth.email_verified}{" "}
            email-verified
            {latestMonth.registered > 0 ? (
              <>
                {" "}
                (
                {Math.round(
                  (latestMonth.email_verified / latestMonth.registered) * 100
                )}
                %)
              </>
            ) : null}
            ,{" "}
            {latestMonth.first_dossier_purchased === null
              ? "— purchased (awaiting Story 2.X)"
              : `${latestMonth.first_dossier_purchased} purchased`}
            .
          </p>
        ) : null}

        <div className="space-y-1.5">
          {data.signup_funnel.map((row) => (
            <div
              key={row.month}
              className="flex items-center gap-3"
              title={`${row.month}: ${row.registered} registered / ${row.email_verified} verified / ${row.first_dossier_purchased ?? "—"} purchased`}
            >
              <span className="font-mono text-xs w-20 opacity-80">
                {row.month}
              </span>
              <div className="flex-1 h-4 bg-brand-paper-100 rounded-sm overflow-hidden flex">
                <div
                  style={{
                    width: `${(row.registered / maxFunnel) * 100}%`,
                  }}
                  className="h-full bg-brand-forest-300"
                  aria-label={`${row.registered} registered`}
                />
                <div
                  style={{
                    width: `${(row.email_verified / maxFunnel) * 100}%`,
                  }}
                  className="h-full bg-brand-forest-700"
                  aria-label={`${row.email_verified} email-verified`}
                />
                {row.first_dossier_purchased !== null ? (
                  <div
                    style={{
                      width: `${(row.first_dossier_purchased / maxFunnel) * 100}%`,
                    }}
                    className="h-full bg-tier-premium-fill"
                    aria-label={`${row.first_dossier_purchased} first-dossier-purchased`}
                  />
                ) : null}
              </div>
              <span className="font-mono text-xs w-32 text-right tabular-nums opacity-80">
                {row.registered} / {row.email_verified} /{" "}
                {row.first_dossier_purchased ?? "—"}
              </span>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-xs pt-2 opacity-80">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-brand-forest-300 rounded-sm" />
            Registered
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-brand-forest-700 rounded-sm" />
            Email-verified
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-tier-premium-fill rounded-sm" />
            First dossier purchased{" "}
            <em className="opacity-70">(awaits Story 2.X)</em>
          </span>
        </div>
      </section>
    </div>
  );
}
